import clamp from 'lodash/clamp';

import {
	getLocalIdsFromSelection,
	getSliceFromSelection,
	isSelectionAtEndOfNode,
	isSelectionAtStartOfNode,
} from '@atlaskit/editor-common/selection';
import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { Decoration, DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { getPluginState } from '../../pm-plugins/decoration/decoration-plugin-factory';
import { SELECTION_CATEGORY } from './analytics/enums';
import type { NodeAndMarkCounts } from './analytics/enums';
import type { Positions } from './xstate/get-ai-experience-service';

function findFirstDeco(decorationSet: DecorationSet, key: string): Decoration | undefined {
	const found = decorationSet.find(undefined, undefined, (spec) => spec.key === key);
	return found.length > 0 ? found[0] : undefined;
}

export function getAIHighlightPositions({
	modalDecorationSet,
}: {
	modalDecorationSet?: DecorationSet;
}): [number, number] | undefined {
	if (!modalDecorationSet) {
		return undefined;
	}

	const inlineDecoration = findFirstDeco(modalDecorationSet, 'inlineDecoration');
	if (inlineDecoration) {
		return [inlineDecoration.from, inlineDecoration.to];
	}

	const startWidget = findFirstDeco(modalDecorationSet, 'generatedStartWidget');
	const endWidget = findFirstDeco(modalDecorationSet, 'generatedEndWidget');

	if (startWidget && endWidget) {
		return [startWidget.from, endWidget.to];
	}

	return undefined;
}

/**
 * Determines the selection category based on the editor state and positions.
 *
 * @param {EditorView} editorView - The editor state.
 * @param {Positions} positions - The positions of the selection.
 * @returns {SELECTION_CATEGORY} The selection category.
 *
 * @example
 * const state = editorView.state;
 * const positions = [10, 20];
 * const category = getSelectionCategoryUsingState(state, positions);
 */
export function getSelectionCategory(
	editorView: EditorView,
	positions: Positions,
): SELECTION_CATEGORY {
	const { state } = editorView;
	return getSelectionCategoryUsingState(state, positions);
}

/**
 * Determines the selection category based on the editor state and positions.
 *
 * @param {EditorState} state - The editor state.
 * @param {Positions} positions - The positions of the selection.
 * @returns {SELECTION_CATEGORY} The selection category.
 *
 * @example
 * const state = editorView.state;
 * const positions = [10, 20];
 * const category = getSelectionCategoryUsingState(state, positions);
 */
export function getSelectionCategoryUsingState(
	state: EditorState,
	positions: Positions,
): SELECTION_CATEGORY {
	const [start, end] = positions;

	const { isInDocumentStreamingEnabled } = getPluginState(state);

	const isEmptySelection = start === end;
	// For in-document streaming, somehow during streaming, the position can be larger than the document size
	// So we clamp the position to the document size to quickly bypass the issue
	// In the future, when we refactor the decoration plugin, we should revisit this if needed
	const $start = isInDocumentStreamingEnabled
		? state.doc.resolve(clamp(start, 1, state.doc.content.size))
		: state.doc.resolve(start);
	const $end = isInDocumentStreamingEnabled
		? state.doc.resolve(clamp(end, 1, state.doc.content.size))
		: state.doc.resolve(end);

	const isSameParent = $start.sameParent($end);
	const isBeginningOfDoc = $start.pos === $start.depth;
	const isEndOfDoc = state.doc.nodeSize - $end.pos - 2 === $end.depth;
	const isStartOfParentNode = isSelectionAtStartOfNode($start, $start.parent);
	const isEndOfParentNode = isSelectionAtEndOfNode($end, $end.parent);

	if (isEmptySelection) {
		return SELECTION_CATEGORY.EMPTY;
	}
	if (isBeginningOfDoc && isEndOfDoc) {
		return SELECTION_CATEGORY.FULL_DOCUMENT;
	}
	if (isSameParent) {
		if (isStartOfParentNode && isEndOfParentNode) {
			return SELECTION_CATEGORY.FULL_SINGLE_BLOCK;
		}
		return SELECTION_CATEGORY.PARTIAL_SINGLE_BLOCK;
	}
	if (isStartOfParentNode && isEndOfParentNode) {
		return SELECTION_CATEGORY.FULL_MULTIPLE_BLOCKS;
	}
	return SELECTION_CATEGORY.PARTIAL_MULTIPLE_BLOCKS;
}

export function hasTextualContent(state: EditorState, positions: Positions): boolean {
	const [start, end] = positions;
	if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
		// In-document streaming can leave positions stale relative to the live
		// doc. Clamp to a safe range to avoid ProseMirror crashes inside
		// textBetween / nodesBetween. See getSelectionCategoryUsingState for the
		// same pattern.
		const docSize = state.doc.content.size;
		const safeStart = clamp(start, 0, docSize);
		const safeEnd = clamp(end, safeStart, docSize);
		const text = state.doc.textBetween(safeStart, safeEnd);
		return text.trim().length > 0;
	} else {
		const text = state.doc.textBetween(start, end);
		return text.trim().length > 0;
	}
}

/**
 * Gets a map of node and mark type names and their count occurrences within the selection.
 *
 * This function traverses the selected content and counts each unique node and mark type
 * in the selection. This can be used for analytics to track what types of content
 * is being selected.
 *
 * @param {EditorState} state - The editor state.
 * @param {Positions} positions - The positions of the selection [start, end].
 * @returns {NodeAndMarkCounts} A map of node/mark type names to their counts.
 *
 * @example
 * const state = editorView.state;
 * const positions = [10, 20];
 * const nodeTypes = getSelectionNodeTypes(state, positions);
 * // Returns: { paragraph: 1, text: 5, strong: 2, link: 1 }
 */
export function getSelectionNodeTypes(state: EditorState, positions: Positions): NodeAndMarkCounts {
	const [start, end] = positions;
	const nodeCounts: NodeAndMarkCounts = {};

	const countNode = (node: PMNode) => {
		// Count the node type
		nodeCounts[node.type.name] = (nodeCounts[node.type.name] ?? 0) + 1;

		// Count each mark on the node
		node.marks.forEach((mark) => {
			nodeCounts[mark.type.name] = (nodeCounts[mark.type.name] ?? 0) + 1;
		});
	};

	if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
		// In-document streaming can leave positions stale relative to the live
		// doc. Walking the tree with out-of-range positions crashes inside
		// ProseMirror's nodesBetween on `undefined.nodeSize`, which then trips
		// the surrounding React error boundary (e.g. <AnalyticsBridge>) and
		// unmounts the AI loading UI. Clamp to a safe range first. See
		// getSelectionCategoryUsingState for the same pattern.
		const docSize = state.doc.content.size;
		const safeStart = clamp(start, 0, docSize);
		const safeEnd = clamp(end, safeStart, docSize);
		state.doc.nodesBetween(safeStart, safeEnd, countNode);
	} else {
		// Walk through the document between the start and end positions
		state.doc.nodesBetween(start, end, countNode);
	}

	return nodeCounts;
}

/**
 * Describes the target of a backend-applied edit, and the surrounding content the
 * backend must retain around it.
 *
 * `prefixDoc` / `suffixDoc` are always either both present or both absent. When
 * present, the backend can reconstruct the complete new content of the edge targets
 * by concatenation:
 *
 * - first target: `prefix` + generated content for that target
 * - last target:  generated content for that target + `suffix`
 *
 * When absent, the selection could not be tied to the edge targets' inline content,
 * so the targets must be addressed as whole nodes instead. Edge presence is the
 * authoritative signal for that: a whole-document selection resolves both ends to
 * the doc node, so it carries no edges and must be replaced wholesale.
 */
type ApplyContextBase = {
	/** The selected text, carried for logging and as a backend assertion. */
	selectedText?: string;
	/** `localId`s of the nodes the edit targets, in document order. */
	targetLocalIds: string[];
};

type ApplyContextWithEdges = {
	/** A doc wrapping the first target node, holding inline content preceding the selection. */
	prefixDoc: PMNode;
	/** A doc wrapping the last target node, holding inline content following the selection. */
	suffixDoc: PMNode;
};

type ApplyContextWithoutEdges = {
	prefixDoc?: never;
	suffixDoc?: never;
};

export type ApplyContext = ApplyContextBase & (ApplyContextWithEdges | ApplyContextWithoutEdges);

/**
 * Wraps `content` in `node`'s own type inside a doc, purely so the request builder
 * can encode it with the existing ADF serializer and read the inline content back
 * out. Only that inline content is sent - the wrapper's type, attrs and marks are
 * discarded, since a consumer rebuilds the node it already holds.
 *
 * Returns `undefined` when the schema rejects the content, or when `createAndFill`
 * had to add content to satisfy the schema: a node that is not in the document must
 * never appear in the edges a consumer may write back.
 */
function wrapInlineContentInDoc(
	state: EditorState,
	node: PMNode,
	content: Fragment,
): PMNode | undefined {
	const wrapper = node.type.createAndFill(node.attrs, content, node.marks);
	if (!wrapper || !wrapper.content.eq(content)) {
		return undefined;
	}

	return state.schema.nodes.doc.createAndFill(null, wrapper) ?? undefined;
}

/**
 * Derives backend apply context from a selection, or `undefined` when it is
 * empty or has no addressable localIds.
 *
 * @example
 * const applyContext = getApplyContext(editorView.state, [10, 20]);
 * // { targetLocalIds: ['abc'], selectedText: 'world', prefixDoc, suffixDoc }
 */
export function getApplyContext(
	state: EditorState,
	positions: Positions,
): ApplyContext | undefined {
	const [start, end] = positions;

	// Nothing is selected, so there is no edit to describe.
	if (start === end) {
		return undefined;
	}

	// In-document streaming can leave positions stale relative to the live doc.
	// Clamp to a safe range first. See getSelectionCategoryUsingState for the
	// same pattern.
	const docSize = state.doc.content.size;
	const safeStart = clamp(start, 0, docSize);
	const safeEnd = clamp(end, safeStart, docSize);

	if (safeStart === safeEnd) {
		return undefined;
	}

	const $start = state.doc.resolve(safeStart);
	const $end = state.doc.resolve(safeEnd);

	const selection = TextSelection.between($start, $end);
	const targetLocalIds = getLocalIdsFromSelection(selection) ?? [];
	if (targetLocalIds.length === 0) {
		return undefined;
	}

	// A localId has to identify exactly one node for any of this to be addressable.
	// ADF does not guarantee uniqueness - copying and pasting a node duplicates its
	// localId - and a repeated id would let a consumer resolve the wrong node, and so
	// splice an edge into it. Refuse rather than guess.
	if (new Set(targetLocalIds).size !== targetLocalIds.length) {
		return undefined;
	}

	// Every selected node has to be addressed, not just most of them.
	// getLocalIdsFromSelection skips a node that carries no localId, which would leave
	// the targets describing a subset of what was selected while the generated content
	// covers all of it - so the skipped node's edit would have nowhere to go.
	if (targetLocalIds.length !== getSliceFromSelection(selection).childCount) {
		return undefined;
	}

	const selectedText = state.doc.textBetween(safeStart, safeEnd, '\n') || undefined;

	// The edges are only reconstructable by concatenation when the selection begins
	// and ends inside inline content whose containing node is itself an edge target.
	// Otherwise the targets are addressed as whole nodes, so there is no partial
	// content to preserve around the edit.
	const anchorsEdges =
		$start.parent.isTextblock &&
		$end.parent.isTextblock &&
		targetLocalIds[0] === $start.parent.attrs?.localId &&
		targetLocalIds[targetLocalIds.length - 1] === $end.parent.attrs?.localId;

	if (!anchorsEdges) {
		return { selectedText, targetLocalIds };
	}

	const prefixDoc = wrapInlineContentInDoc(
		state,
		$start.parent,
		state.doc.slice($start.start(), safeStart).content,
	);
	const suffixDoc = wrapInlineContentInDoc(
		state,
		$end.parent,
		state.doc.slice(safeEnd, $end.end()).content,
	);

	// Both edges or neither: a consumer cannot tell an omitted edge apart from an
	// empty one, so a half-described selection must not be sent.
	if (!prefixDoc || !suffixDoc) {
		return { selectedText, targetLocalIds };
	}

	return { prefixDoc, selectedText, suffixDoc, targetLocalIds };
}
